// ProspeccionImportarGrilla.jsx — Carga masiva de prospectos tipo hoja de cálculo.
// Una fila por prospecto. Desplegables por fila (segmento/industria/origen y ubicación GeoRef).
// Soporta pegar bloques desde Excel/Sheets (TSV) desde la celda enfocada.

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { PROVINCIAS_AR, fetchLocalidadesAr } from "./georefAr";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";
const PAISES = ["Argentina", "Chile", "Uruguay", "Brasil", "Paraguay", "Bolivia", "Otro"];

const uid = () => Math.random().toString(36).slice(2);

// Columnas (set enfocado). type: text | dim | pais | provincia | ciudad
const COLS = [
  { key: "empresa",           label: "Empresa *",  w: 180, type: "text" },
  { key: "segmento",          label: "Segmento",   w: 130, type: "dim", src: "segmentos" },
  { key: "industria",         label: "Industria",  w: 140, type: "dim", src: "industrias" },
  { key: "origen",            label: "Origen",     w: 130, type: "dim", src: "origenes" },
  { key: "pais",              label: "País",       w: 110, type: "pais" },
  { key: "provincia",         label: "Provincia",  w: 150, type: "provincia" },
  { key: "ciudad",            label: "Ciudad",     w: 150, type: "ciudad" },
  { key: "sitio_web",         label: "Sitio web",  w: 140, type: "text" },
  { key: "instagram",         label: "Instagram",  w: 120, type: "text" },
  { key: "linkedin",          label: "LinkedIn",   w: 120, type: "text" },
  { key: "contacto_nombre",   label: "Contacto",   w: 140, type: "text" },
  { key: "contacto_email",    label: "Email",      w: 170, type: "text" },
  { key: "contacto_telefono", label: "Teléfono",   w: 120, type: "text" },
  { key: "notas",             label: "Notas",      w: 200, type: "text" },
];

const emptyRow = () => {
  const r = { _k: uid() };
  COLS.forEach((c) => { r[c.key] = c.key === "pais" ? "Argentina" : ""; });
  return r;
};

const cellInput = { width: "100%", border: "none", outline: "none", fontSize: 13, padding: "7px 8px", background: "transparent", boxSizing: "border-box", color: "#222", fontFamily: "inherit" };
const cellSelect = { ...cellInput, cursor: "pointer", appearance: "auto" };

const ProspeccionImportarGrilla = () => {
  const history = useHistory();

  const [filas, setFilas] = useState(() => Array.from({ length: 8 }, emptyRow));
  const [dims, setDims] = useState({ segmentos: [], industrias: [], origenes: [] });
  const [locByProv, setLocByProv] = useState({}); // { provincia: [nombres] }
  const [existentes, setExistentes] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const filasRef = useRef(filas);
  filasRef.current = filas;

  // Cargar catálogos + duplicados + provincias AR
  useEffect(() => {
    axios.get(`${BASE}/buscar_segmentos.php`).then((r) => setDims((d) => ({ ...d, segmentos: r.data || [] }))).catch(() => {});
    axios.get(`${BASE}/buscar_industrias.php`).then((r) => setDims((d) => ({ ...d, industrias: r.data || [] }))).catch(() => {});
    axios.get(`${BASE}/buscar_origenes_prospeccion.php`).then((r) => setDims((d) => ({ ...d, origenes: r.data || [] }))).catch(() => {});
    axios.post(`${BASE}/prospectos_empresas_existentes.php`).then((r) => setExistentes(new Set(Array.isArray(r.data) ? r.data : []))).catch(() => {});
  }, []);

  const ensureLocalidades = (prov) => {
    if (!prov || locByProv[prov]) return;
    fetchLocalidadesAr(prov).then((nombres) => {
      if (nombres.length > 0) setLocByProv((m) => ({ ...m, [prov]: nombres }));
    });
  };

  // Setear una celda; agrega fila vacía al final si la última tiene empresa
  const setCell = (ri, key, val) => {
    setFilas((prev) => {
      const next = prev.map((f, i) => (i === ri ? { ...f, [key]: val } : f));
      if (key === "pais") { next[ri].provincia = ""; next[ri].ciudad = ""; }
      if (key === "provincia") { next[ri].ciudad = ""; ensureLocalidades(val); }
      const last = next[next.length - 1];
      if (last.empresa.trim() !== "") next.push(emptyRow());
      return next;
    });
  };

  const removeRow = (ri) => setFilas((prev) => (prev.length <= 1 ? [emptyRow()] : prev.filter((_, i) => i !== ri)));

  // ── Pegar bloque TSV desde (ri, ci) ──────────────────────────────────────────
  const handlePaste = (ri, ci) => (e) => {
    const text = e.clipboardData?.getData("text/plain") ?? "";
    if (!text.includes("\t") && !text.includes("\n")) return; // pegado simple → comportamiento normal
    e.preventDefault();
    const matriz = text.replace(/\r/g, "").replace(/\n$/, "").split("\n").map((l) => l.split("\t"));
    setFilas((prev) => {
      const next = prev.map((f) => ({ ...f }));
      matriz.forEach((cels, dr) => {
        const rIdx = ri + dr;
        while (next.length <= rIdx) next.push(emptyRow());
        cels.forEach((cel, dc) => {
          const col = COLS[ci + dc];
          if (!col) return;
          next[rIdx][col.key] = cel.trim();
          if (col.type === "provincia") ensureLocalidades(cel.trim());
        });
      });
      // asegurar fila vacía final
      if (next[next.length - 1].empresa.trim() !== "") next.push(emptyRow());
      return next;
    });
  };

  const esDuplicada = (empresa) => empresa.trim() && existentes.has(empresa.trim().toLowerCase());
  const conEmpresa = filas.filter((f) => f.empresa.trim() !== "");

  const guardar = async () => {
    if (conEmpresa.length === 0) { toast.error("Cargá al menos un prospecto con empresa."); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${BASE}/prospectos_importar_grilla.php`, new URLSearchParams({
        filas: JSON.stringify(conEmpresa.map(({ _k, ...rest }) => rest)),
      }));
      if (r.data?.success) {
        const dup = r.data.duplicados?.length ? ` · ${r.data.duplicados.length} duplicado(s) salteado(s)` : "";
        toast.success(`${r.data.insertados} prospecto(s) importado(s)${dup}`);
        history.push("/dashboard/crm/prospeccion");
      } else {
        toast.error(r.data?.mensaje || "Error al importar");
      }
    } catch { toast.error("Error de conexión"); }
    finally { setSaving(false); }
  };

  // ── Render de una celda ──────────────────────────────────────────────────────
  const renderCell = (fila, ri, col, ci) => {
    const val = fila[col.key] ?? "";
    const onPaste = handlePaste(ri, ci);

    if (col.type === "dim") {
      const opts = dims[col.src] || [];
      const falta = val && !opts.some((o) => o.nombre === val);
      return (
        <select style={cellSelect} value={val} onChange={(e) => setCell(ri, col.key, e.target.value)}>
          <option value=""></option>
          {falta && <option value={val}>{val}</option>}
          {opts.map((o) => <option key={o.id} value={o.nombre}>{o.nombre}</option>)}
        </select>
      );
    }
    if (col.type === "pais") {
      return (
        <select style={cellSelect} value={val || "Argentina"} onChange={(e) => setCell(ri, col.key, e.target.value)}>
          {PAISES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      );
    }
    if (col.type === "provincia") {
      if ((fila.pais || "Argentina") === "Argentina") {
        const falta = val && !PROVINCIAS_AR.includes(val);
        return (
          <select style={cellSelect} value={val} onChange={(e) => setCell(ri, col.key, e.target.value)}>
            <option value=""></option>
            {falta && <option value={val}>{val}</option>}
            {PROVINCIAS_AR.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        );
      }
      return <input style={cellInput} value={val} onChange={(e) => setCell(ri, col.key, e.target.value)} onPaste={onPaste} />;
    }
    if (col.type === "ciudad") {
      const listId = `loc_${ri}`;
      const locs = locByProv[fila.provincia] || [];
      return (
        <>
          <input style={cellInput} value={val} list={listId} onFocus={() => ensureLocalidades(fila.provincia)}
                 onChange={(e) => setCell(ri, col.key, e.target.value)} onPaste={onPaste} />
          {locs.length > 0 && <datalist id={listId}>{locs.map((n) => <option key={n} value={n} />)}</datalist>}
        </>
      );
    }
    // text
    return <input style={cellInput} value={val} onChange={(e) => setCell(ri, col.key, e.target.value)} onPaste={onPaste} />;
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f7f4f6", padding: "24px 20px 60px", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 1500, margin: "0 auto" }}>
        {/* Header */}
        <button onClick={() => history.push("/dashboard/crm/prospeccion")}
          style={{ background: "none", border: "none", color: "#9f0059", fontSize: 13, fontWeight: 600, cursor: "pointer", marginBottom: 12 }}>
          ← Volver al embudo
        </button>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12, marginBottom: 8 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#222" }}>Importar prospectos</h1>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#888" }}>
              Pegá columnas desde Excel y completá los desplegables. Solo <b>Empresa</b> es obligatoria.
            </p>
          </div>
          <button onClick={guardar} disabled={saving}
            style={{ padding: "10px 22px", background: "#9f0059", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: saving ? "wait" : "pointer", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Importando…" : `Importar ${conEmpresa.length} prospecto${conEmpresa.length !== 1 ? "s" : ""}`}
          </button>
        </div>

        {/* Grilla */}
        <div style={{ overflowX: "auto", background: "#fff", borderRadius: 12, border: "1.5px solid #f0e6ec", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", marginTop: 16 }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 1200 }}>
            <thead>
              <tr>
                <th style={{ width: 34, background: "#9f0059" }}></th>
                {COLS.map((c) => (
                  <th key={c.key} style={{ minWidth: c.w, background: "#9f0059", color: "#fff", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", padding: "9px 8px", textAlign: "left", whiteSpace: "nowrap" }}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((fila, ri) => {
                const dup = esDuplicada(fila.empresa);
                return (
                  <tr key={fila._k} style={{ background: dup ? "#fef2f2" : (ri % 2 === 0 ? "#fff" : "#fdf8fb") }}>
                    <td style={{ textAlign: "center", borderBottom: "1px solid #f0e6ec", borderRight: "1px solid #f0e6ec" }}>
                      <button onClick={() => removeRow(ri)} title="Quitar fila" style={{ background: "none", border: "none", color: "#c0396a", cursor: "pointer", fontSize: 13 }}>✕</button>
                    </td>
                    {COLS.map((col, ci) => (
                      <td key={col.key} title={col.key === "empresa" && dup ? "Ya existe un prospecto/cliente con esta empresa" : undefined}
                          style={{ borderBottom: "1px solid #f0e6ec", borderRight: "1px solid #f5eef2", padding: 0 }}>
                        {renderCell(fila, ri, col, ci)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <button onClick={() => setFilas((p) => [...p, ...Array.from({ length: 5 }, emptyRow)])}
            style={{ background: "none", border: "1.5px dashed #c990b0", borderRadius: 8, color: "#9f0059", padding: "8px 16px", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
            + Agregar 5 filas
          </button>
          <span style={{ fontSize: 12, color: "#999" }}>Las filas en rojo son empresas que ya existen (se saltean al importar).</span>
        </div>
      </div>
    </div>
  );
};

export default ProspeccionImportarGrilla;
